<template>
  <div class="vendor vendor-simple col-sm-12">
    <div class="vendor-title pointer" @click="toggleBlock">
      <strong>
        <span class="tc-title">{{ vendor.name }}</span>
        <span v-if="displayMore" class="chevron glyphicon glyphicon-chevron-up" aria-hidden="true"></span>
        <span v-else class="chevron glyphicon glyphicon-chevron-down" aria-hidden="true"></span>
      </strong>
      <div @click.stop class="right-container">
        <div v-if="hasLegIntToggle" class="switch-container">
          <label v-if="settings.iabLegitimateInterestToggle">{{ $t('legitimateInterest') }}</label>
          <pc-checkbox-button
            v-if="settings.consentButtonStyle === 'button'"
            v-model="vendor.legitimateInterest"
            :aria-label="vendor.label"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-checkbox-button>
          <pc-checkbox-native
            v-else-if="settings.consentButtonStyle === 'checkbox'"
            :aria-label="$t('enableVendorAria', { label: stripHtml(vendor.label) })"
            v-model="vendor.legitimateInterest"
            @input="emitConsentChanged"
          ></pc-checkbox-native>
          <pc-leg-switch
            v-else
            v-model="vendor.legitimateInterest"
            :settings="settings"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-leg-switch>
        </div>
        <div
          class="switch-container"
          v-if="!hasNoConsentToggle"
          :class="{ 'visibility-hidden': !hasConsentToggleVisible }"
        >
          <label v-if="settings.iabLegitimateInterestToggle">{{ $t('consent') }}</label>
          <pc-checkbox-button
            v-if="settings.consentButtonStyle === 'button'"
            v-model="vendor.consent"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-checkbox-button>
          <pc-checkbox-native
            v-else-if="settings.consentButtonStyle === 'checkbox'"
            v-model="vendor.consent"
            @input="emitConsentChanged"
          ></pc-checkbox-native>
          <pc-leg-switch
            v-else
            v-model="vendor.consent"
            :settings="settings"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-leg-switch>
        </div>
      </div>
    </div>
    <div class="vendor-details" v-if="displayMore">
      <div class="detail-margin" v-if="vendor.policyUrl">
        <strong class="policy-title">{{ $t('privacyPolicy') }}:</strong>
        <a class="vendor-policy tc-description" :href="vendor.policyUrl" target="_blank" rel="noopener noreferrer">
          {{ vendor.policyUrl }}
        </a>
      </div>
      <div class="detail-margin" v-if="vendor.legIntPolicyUrl">
        <strong class="policy-title">{{ $t('legIntPrivacyPolicy') }}:</strong>
        <a
          class="vendor-policy tc-description"
          :href="vendor.legIntPolicyUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ vendor.legIntPolicyUrl }}
        </a>
      </div>
      <div class="vendor-details-box" v-if="vendor.purposes.length > 0">
        <strong>{{ $t('purposes') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.purposes" :key="'vp' + id" aria-modal="true" tabindex="0">
            <div class="link" @click="toggleQuote('vendor_purposes', id)">
              {{ iab.vendorList.purposes[id].name }}&nbsp;ⓘ
            </div>
            <span v-if="vendor.dataRetention && vendor.dataRetention.purposes">
              {{ computeRetentionDuration(vendor.dataRetention.purposes[id]) }}
            </span>
            <blockquote v-if="displayedQuotes['vendor_purposes'][id]">
              {{ iab.vendorList.purposes[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="vendor.specialPurposes.length > 0">
        <strong>{{ $t('specialPurposes') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.specialPurposes" :key="'vsp' + id" aria-modal="true" tabindex="0">
            <div class="link" @click="toggleQuote('vendor_specialPurposes', id)">
              {{ iab.vendorList.specialPurposes[id].name }}&nbsp;ⓘ
            </div>
            <span v-if="vendor.dataRetention && vendor.dataRetention.specialPurposes">
              {{ computeRetentionDuration(vendor.dataRetention.specialPurposes[id]) }}
            </span>
            <blockquote v-if="displayedQuotes['vendor_specialPurposes'][id]">
              {{ iab.vendorList.specialPurposes[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="vendor.features.length > 0">
        <strong>{{ $t('features') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.features" :key="'vsp' + id" aria-modal="true" tabindex="0">
            <div class="link" @click="toggleQuote('vendor_features', id)">
              {{ iab.vendorList.features[id].name }}&nbsp;ⓘ
            </div>
            <blockquote v-if="displayedQuotes['vendor_features'][id]">
              {{ iab.vendorList.features[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="vendor.specialFeatures.length > 0">
        <strong>{{ $t('specialFeatures') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.specialFeatures" :key="'vsp' + id" aria-modal="true" tabindex="0">
            <div class="link" @click="toggleQuote('vendor_specialFeatures', id)">
              {{ iab.vendorList.specialFeatures[id].name }}&nbsp;ⓘ
            </div>
            <blockquote v-if="displayedQuotes['vendor_specialFeatures'][id]">
              {{ iab.vendorList.specialFeatures[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="vendor.legIntPurposes.length > 0">
        <strong>{{ $t('legitimateInterests') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.legIntPurposes" :key="'vli' + id">
            <div class="link" @click="toggleQuote('vendor_legIntPurposes', id)">
              {{ iab.vendorList.purposes[id].name }}&nbsp;ⓘ
            </div>
            <blockquote v-if="displayedQuotes['vendor_legIntPurposes'][id]">
              {{ iab.vendorList.purposes[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div
        class="vendor-details-box"
        v-if="iab.vendorList.dataCategories && vendor.dataDeclaration && vendor.dataDeclaration.length > 0"
      >
        <strong>{{ $t('dataDeclaration') }}:</strong>
        <ul role="list">
          <li v-for="id in vendor.dataDeclaration" :key="'dc' + id" aria-modal="true" tabindex="0">
            <div class="link" @click="toggleQuote('vendor_dataDeclaration', id)">
              {{ iab.vendorList.dataCategories[id].name }}&nbsp;ⓘ
            </div>
            <blockquote v-if="displayedQuotes['vendor_dataDeclaration'][id]">
              {{ iab.vendorList.dataCategories[id].description }}
            </blockquote>
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="displayStorageType">
        <strong>{{ $t('storageType') }}:</strong>
        <ul role="list">
          <li v-if="vendor.cookieMaxAgeSeconds >= 0" aria-modal="true" tabindex="0">
            {{ $t('cookies') }}
          </li>
          <li v-if="vendor.usesNonCookieAccess" aria-modal="true" tabindex="0">
            {{ $t('otherStorage') }}
          </li>
        </ul>
      </div>
      <div class="vendor-details-box" v-if="vendor.cookieMaxAgeSeconds >= 0">
        <strong>{{ $t('cookieMaxAge') }}:</strong>
        <div v-if="!vendor.cookieMaxAgeSeconds">
          {{ humanizedCookieMaxAge }}
        </div>
        <div v-if="vendor.cookieMaxAgeSeconds">
          {{ humanizedCookieMaxAge }}&nbsp;({{ vendor.cookieMaxAgeSeconds }} {{ $t('seconds') }})
        </div>
      </div>
      <div class="vendor-details-box" v-if="deviceStorageDisclosures">
        <strong>{{ $t('storageDetails') }}:</strong>
        <ul role="list" v-for="(disclosure, index) of deviceStorageDisclosures" :key="disclosure.identifier + index">
          <li aria-modal="true" tabindex="0">
            <strong>{{ $t('storageIdentifier') }}:&nbsp;{{ disclosure.identifier }}</strong>
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t('storageType') }}</th>
                  <th>{{ $t('storageMaxAge') }}</th>
                  <th>{{ $t('storageDomain') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {{ disclosure.type }}
                  </td>
                  <td>
                    <div v-if="!disclosure.maxAgeSeconds">
                      {{ humanizeDuration(disclosure.maxAgeSeconds) }}
                    </div>
                    <div v-if="disclosure.maxAgeSeconds">
                      {{ humanizeDuration(disclosure.maxAgeSeconds) }}&nbsp;({{ disclosure.maxAgeSeconds }}
                      {{ $t('seconds') }})
                    </div>
                  </td>
                  <td>
                    {{ disclosure.domain }}
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="vendor-details-box" v-if="deviceStorageDisclosures">
              <strong>{{ $t('purposes') }}:</strong>
              <ul role="list">
                <li v-for="id of disclosure.purposes" :key="'storage' + id" aria-modal="true" tabindex="0">
                  <div class="link" @click="toggleQuote('disclosure_purposes', id)">
                    {{ iab.vendorList.purposes[id].name }}&nbsp;ⓘ
                  </div>
                  <blockquote v-if="displayedQuotes['disclosure_purposes'][id]">
                    {{ iab.vendorList.purposes[id].description }}
                  </blockquote>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import PcCheckboxButton from '@/components/forms/pc-checkbox-button';
import PcLegSwitch from '@/components/forms/pc-leg-switch';
import PcCheckboxNative from '@/components/forms/pc-checkbox-native';

import { i18nMixin } from 'shared/i18n/i18n-mixin';
import { eventEmitterMixin } from '@/common/messaging/event-emitter-mixin';
import humanizeDuration from 'humanize-duration';
import fetch from 'unfetch';
import { stripHtml } from '@/common/filters/strip-html';

export default {
  components: {
    PcCheckboxButton,
    PcLegSwitch,
    PcCheckboxNative,
  },
  mixins: [i18nMixin, eventEmitterMixin],
  props: {
    settings: {
      type: Object,
      default: () => {},
    },
    vendor: {
      type: Object,
      default: () => ({
        name: '',
      }),
    },
    iab: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      displayMore: false,
      deviceStorageDisclosures: null,
      displayedQuotes: {
        vendor_purposes: [],
        vendor_specialPurposes: [],
        vendor_features: [],
        vendor_specialFeatures: [],
        vendor_legIntPurposes: [],
        vendor_dataDeclaration: [],
        disclosure_purposes: [],
      },
    };
  },
  computed: {
    hasLegIntToggle() {
      return (
        (this.settings.iabLegitimateInterestToggle || !this.hasConsentToggleVisible) && this.vendor.isLegitimateInterest
      );
    },
    hasConsentToggleVisible() {
      return this.vendor.purposes.length > 0;
    },
    hasNoConsentToggle() {
      return !this.settings.iabLegitimateInterestToggle && this.hasLegIntToggle;
    },
    humanizedCookieMaxAge() {
      return this.humanizeDuration(this.vendor.cookieMaxAgeSeconds);
    },
    displayStorageType() {
      return this.vendor.cookieMaxAgeSeconds >= 0 || this.vendor.usesNonCookieAccess === true;
    },
  },
  watch: {
    'vendor.consent': function(value) {
      if (!this.settings.iabLegitimateInterestToggle && !this.hasLegIntToggle && this.vendor.isLegitimateInterest) {
        this.vendor.legitimateInterest = value;
      }
    },
  },
  methods: {
    stripHtml,
    computeRetentionDuration(specificDuration) {
      let duration;

      if (specificDuration != null) {
        duration = specificDuration;
      }

      if (this.vendor.dataRetention.stdRetention != null) {
        duration = this.vendor.dataRetention.stdRetention;
      }

      if (duration != null) {
        duration = duration * 86400;
        return ` (${this.humanizeDuration(duration)})`;
      }

      return;
    },
    humanizeDuration(seconds) {
      if (seconds != null) {
        if (seconds === 0) {
          return this.$t('endOfSession');
        }

        let locale = this.$getLocale();
        if (['pt-pt', 'pt-br'].indexOf(locale) !== -1) {
          locale = 'pt';
        }
        return humanizeDuration(seconds * 1000, { language: locale });
      }
      return 'N/A';
    },
    toggleBlock() {
      if (this.vendor.deviceStorageDisclosureUrl && this.deviceStorageDisclosure == null) {
        fetch(this.vendor.deviceStorageDisclosureUrl)
          .then(response => response.json())
          .then(json => (this.deviceStorageDisclosures = json.disclosures));
      }
      this.displayMore = !this.displayMore;
    },
    toggleQuote(type, id) {
      this.displayedQuotes[type][id] = !this.displayedQuotes[type][id];
    },
  },
};
</script>
<style lang="less">
.visibility-hidden {
  visibility: hidden;
  .btn-toggle.default-neutral > span {
    visibility: hidden;
  }
}

.detail-margin {
  //when there is no ul
  margin-bottom: 0.6rem;
}

.tc-storage-detail {
  margin-top: 2rem;
  font-weight: 600;
}

.pl-0 {
  padding-left: 0px;
}
.vendor-details-box {
  display: flex;
  flex-direction: column;
  gap: 1em;
}
.vendor-details-box > ul {
  display: flex;
  flex-direction: column;
  gap: 1em;
  padding-left: 2em;
  margin: 0;
}
.vendor-details-box > ul > li {
  cursor: pointer;
}
.vendor-details-box > ul > li > .link:hover {
  text-decoration: underline;
}
.vendor-details-box > ul > li > blockquote {
  padding: 0.5em;
  font-size: inherit;
  border: 1px solid #eeeeee;
  margin: 0;
}
</style>
